import { useState } from "react";
import { motion } from "framer-motion";
import { Handshake, Gamepad2, MessageSquare, Sparkles, Mail, ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const TYPES = [
  { id: "Brand Partnerships", icon: Handshake },
  { id: "Game Development", icon: Gamepad2 },
  { id: "UGC Creation", icon: Sparkles },
  { id: "General Inquiry", icon: MessageSquare },
];

const DEFAULT_EMAILS = ["jasper@asia.com", "stars@post.com"];

export const Campaign = ({ site = {} }) => {
  const emails = (site.campaign_emails && site.campaign_emails.length ? site.campaign_emails : DEFAULT_EMAILS);
  const eyebrow = site.campaign_eyebrow || "Start a Campaign";
  const heading = site.campaign_heading || "Let's build something loud.";
  const body = site.campaign_body || "Brand partnerships, game development, UGC drops or a general hello — tell us what you have in mind and the Etoire team will get back to you.";
  const [type, setType] = useState("Brand Partnerships");
  const [form, setForm] = useState({ full_name: "", email: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inquiries", { inquiry_type: type, ...form });
      setDone(true);
      toast.success("Message sent — we'll be in touch.");
      setForm({ full_name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-background border border-border focus:border-volt outline-none px-4 py-3 text-sm transition-colors duration-200";
  const labelCls = "font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground";

  return (
    <section id="campaign" className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-36">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">{eyebrow}</span>
          <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight uppercase mt-6 leading-[1.05]">
            {heading}
          </h2>
          <p className="text-muted-foreground leading-relaxed mt-6 max-w-md">
            {body}
          </p>
          <div className="mt-10 space-y-3">
            <span className={labelCls}>Business contact</span>
            {emails.map((em) => (
              <a key={em} href={`mailto:${em}`} data-testid={`contact-email-${em}`}
                className="group flex items-center gap-3 border border-border bg-card px-4 py-3 hover:border-volt transition-colors duration-200">
                <Mail className="w-4 h-4 text-volt" />
                <span className="font-mono text-sm">{em}</span>
                <ArrowUpRight className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-volt transition-colors duration-200" />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              data-testid="campaign-success" className="border border-volt bg-card p-12 flex flex-col items-center justify-center text-center h-full min-h-[400px]">
              <div className="w-14 h-14 flex items-center justify-center bg-volt text-white mb-6"><Check className="w-7 h-7" /></div>
              <h3 className="font-display font-bold text-2xl uppercase tracking-tight">Message received</h3>
              <p className="text-muted-foreground mt-3 max-w-sm">Thanks for reaching out. We'll reply to your email as soon as possible.</p>
              <button onClick={() => setDone(false)} data-testid="campaign-reset" className="mt-8 font-mono text-xs tracking-[0.2em] uppercase text-volt hover:underline">Send another</button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="campaign-form" className="border border-border bg-card p-6 md:p-10 space-y-7">
              <div>
                <span className={labelCls}>What's this about?</span>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {TYPES.map((t) => {
                    const Icon = t.icon;
                    const activeSel = type === t.id;
                    return (
                      <button type="button" key={t.id} onClick={() => setType(t.id)} data-testid={`inquiry-type-${t.id.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`flex items-center gap-2.5 border px-4 py-3.5 text-left transition-colors duration-200 ${activeSel ? "border-volt bg-volt/10 text-foreground" : "border-border text-muted-foreground hover:border-white/30"}`}>
                        <Icon className={`w-4 h-4 shrink-0 ${activeSel ? "text-volt" : ""}`} />
                        <span className="font-mono text-[11px] tracking-[0.1em] uppercase leading-tight">{t.id}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className={labelCls}>Full name</label>
                  <input data-testid="campaign-name" className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <label className={labelCls}>Email address</label>
                  <input data-testid="campaign-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} required />
                </div>
              </div>

              <div className="space-y-2">
                <label className={labelCls}>Company name</label>
                <input data-testid="campaign-company" className={inputCls} value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Optional" />
              </div>

              <div className="space-y-2">
                <label className={labelCls}>Message</label>
                <textarea data-testid="campaign-message" rows={5} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} required />
              </div>

              <button type="submit" disabled={busy} data-testid="campaign-submit"
                className="w-full bg-volt text-white font-mono text-xs tracking-[0.2em] uppercase py-4 hover:brightness-110 disabled:opacity-50 transition-all duration-200">
                {busy ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
